import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getAllRegistrosV2ByIdDocenteIdIdiomaIdGrupo,
  actualizarRegistroNotaV2,
  crearRegistroV2,
} from "../../../services/registroV2.service";
import {
  getAllRegistrosByIdDocenteIdIdiomaIdGrupo,
} from "../../../services/registro.service";
import {
  getAsistenciasByDocenteIdiomaGrupoEstado,
  getAllTipoAsistencia,
} from "../../../services/asistencia.service";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import {
  showErrorAlert,
  showActualizadoCorrectamenteAlert,
} from "../../../components/molecules/ShowAlert";
import { descargarNotasGrupo } from "../../../services/grupo.service";

const getLocalStorageKey = (idGrupo, idIdioma) => `notas_grupo_v2_${idGrupo}_${idIdioma}`;

const guardarNotasLocal = (idGrupo, idIdioma, estudiantes) => {
  try {
    const notasData = {
      timestamp: new Date().toISOString(),
      estudiantes: estudiantes.map(est => ({
        idRegistroNotasV2: est.idRegistroNotasV2,
        proyectoIntegrador: est.proyectoIntegrador,
        examenFinalEscrito: est.examenFinalEscrito,
        examenFinalOral: est.examenFinalOral,
        tareas: est.tareas,
        participacion: est.participacion,
        notaFinal: est.notaFinal,
      }))
    };
    localStorage.setItem(getLocalStorageKey(idGrupo, idIdioma), JSON.stringify(notasData));
  } catch (error) {
    console.warn('Error guardando notas localmente:', error);
  }
};

const cargarNotasLocal = (idGrupo, idIdioma) => {
  try {
    const data = localStorage.getItem(getLocalStorageKey(idGrupo, idIdioma));
    if (data) {
      const parsedData = JSON.parse(data);
      const dataDate = new Date(parsedData.timestamp);
      const now = new Date();
      const daysDiff = (now - dataDate) / (1000 * 60 * 60 * 24);
      
      if (daysDiff <= 7) {
        return parsedData.estudiantes;
      } else {
        localStorage.removeItem(getLocalStorageKey(idGrupo, idIdioma));
      }
    }
  } catch (error) {
    console.warn('Error cargando notas localmente:', error);
  }
  return null;
};

const limpiarNotasLocal = (idGrupo, idIdioma) => {
  try {
    localStorage.removeItem(getLocalStorageKey(idGrupo, idIdioma));
  } catch (error) {
    console.warn('Error limpiando notas locales:', error);
  }
};

export const GrupoDocenteDetalle = () => {
  const { idGrupo, idIdioma } = useParams();
  const location = useLocation();
  const { grupoDocente } = location.state || {};

  const [estudiantes, setEstudiantes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasLocalData, setHasLocalData] = useState(false);
  const [asistenciasGrupo, setAsistenciasGrupo] = useState([]);
  const [tiposAsistencia, setTiposAsistencia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Límites máximos por columna según la nueva planilla:
  // Participación 15%, Tareas 20%, Proyecto Integrador 20%,
  // Examen final escrito 20%, Examen final oral 20%, Asistencia 5%.
  const limites = {
    participacion: 15,         // 15%
    tareas: 20,                // 20%
    proyectoIntegrador: 20,    // 20%
    examenFinalEscrito: 20,    // 20%
    examenFinalOral: 20,       // 20%
  };
  const toNullIfZeroOrEmpty = (v, allowZero = false) => {
    if (v === null || v === undefined || v === "") return null;
    const num = Number(v);
    if (Number.isNaN(num)) return null;
    if (!allowZero && num === 0) return null;
    return num;
  };
  const n = (v) => (v === null || v === undefined || Number.isNaN(v) ? 0 : Number(v));
  const getRegistroId = (est) =>
    est?.idRegistroNotasV2 || est?.IdRegistroNotasV2 || est?.idRegistro || est?.IdRegistro;

  const getAsistenciaRegistroId = (a) =>
    a?.idRegistroNotasV2 || a?.IdRegistroNotasV2 || a?.idRegistro || a?.IdRegistro;

  const getSiglaTipoAsistencia = (idTipoAsistencia) => {
    const tipo = tiposAsistencia.find(
      (t) => t?.idTipoAsistencia === idTipoAsistencia || t?.IdTipoAsistencia === idTipoAsistencia
    );
    return (tipo?.sigla || tipo?.Sigla || "").trim().toUpperCase();
  };

  const cuentaComoAsistencia = (idTipoAsistencia) => {
    const sigla = getSiglaTipoAsistencia(idTipoAsistencia);
    return sigla === "P" || sigla === "L" || sigla === "R";
  };

  const calcularAsistenciaNota = (est) => {
    if (!asistenciasGrupo || asistenciasGrupo.length === 0) return { nota: 0, asistidas: 0, total: 0 };

    const idRegistro = getRegistroId(est);
    if (!idRegistro) return { nota: 0, asistidas: 0, total: 0 };

    // Obtener todas las fechas únicas de asistencia para este grupo
    const fechasGrupo = new Set(
      asistenciasGrupo
        .map((a) => a?.fechaAsistencia?.split?.("T")?.[0])
        .filter(Boolean)
    );
    const totalClases = fechasGrupo.size;
    if (totalClases === 0) return { nota: 0, asistidas: 0, total: 0 };

    // Contar cuántas de esas fechas el estudiante estuvo presente (P, L, R)
    const fechasAsistidas = new Set();
    asistenciasGrupo.forEach((a) => {
      const registroAsistenciaId = getAsistenciaRegistroId(a);
      if (registroAsistenciaId !== idRegistro) return;

      if (cuentaComoAsistencia(a?.idTipoAsistencia || a?.IdTipoAsistencia)) {
        const fecha = a?.fechaAsistencia?.split?.("T")?.[0];
        if (fecha) fechasAsistidas.add(fecha);
      }
    });

    const clasesAsistidas = fechasAsistidas.size;
    // Nota sobre 5 puntos
    const nota = (clasesAsistidas * 5) / totalClases;
    return { nota, asistidas: clasesAsistidas, total: totalClases };
  };
  const V = (v) => {
    if (v === null || v === undefined) return "";
    const num = Number(v);
    if (Number.isNaN(num)) return "";
    if (num === 0) return "0";
    return String(num);
  };

  const calcularNivelModulo = (notaFinal) => {
    if (notaFinal === null || notaFinal === undefined || notaFinal === 0) {
      return "NO";
    }
    const nota = Number(notaFinal);
    if (Number.isNaN(nota)) return "NO";
    
    if (nota > 0 && nota <= 50) {
      return "Insuficiente";
    } else if (nota > 50 && nota <= 74) {
      return "Suficiente";
    } else if (nota > 74 && nota <= 89) {
      return "Bueno";
    } else if (nota > 89 && nota <= 94) {
      return "Satisfactorio";
    } else if (nota > 94) {
      return "Excelente";
    }
    return "NO";
  };

  useEffect(() => {
    const docenteId = grupoDocente?.idDocente || grupoDocente?.IdDocente;
    if (!docenteId || !idIdioma || !idGrupo) return;

    const cargarTodo = async () => {
      setIsLoading(true);
      try {
        // Ejecutar TODAS las peticiones en paralelo (5 llamadas al mismo tiempo)
        const [
          dataV2, 
          dataAntiguos, 
          asistenciasProgreso, 
          asistenciasCulminado, 
          tipos
        ] = await Promise.all([
          getAllRegistrosV2ByIdDocenteIdIdiomaIdGrupo(docenteId, idIdioma, idGrupo).catch(() => []),
          getAllRegistrosByIdDocenteIdIdiomaIdGrupo(docenteId, idIdioma, idGrupo).catch(() => []),
          getAsistenciasByDocenteIdiomaGrupoEstado(docenteId, idIdioma, idGrupo, "Progreso").catch(() => []),
          getAsistenciasByDocenteIdiomaGrupoEstado(docenteId, idIdioma, idGrupo, "Culminado").catch(() => []),
          getAllTipoAsistencia().catch(() => [])
        ]);

        // 1. Procesar Asistencias y Tipos
        const todasAsistencias = [...(asistenciasProgreso || []), ...(asistenciasCulminado || [])];
        const asistenciasUnicas = [];
        const idsVistos = new Set();
        todasAsistencias.forEach(a => {
          const id = a.idAsistencia || a.IdAsistencia;
          if (id && !idsVistos.has(id)) {
            idsVistos.add(id);
            asistenciasUnicas.push(a);
          }
        });

        const tiposA = Array.isArray(tipos) ? tipos : [];
        setAsistenciasGrupo(asistenciasUnicas);
        setTiposAsistencia(tiposA);

        // Funciones temporales para usar con los datos frescos
        const getSiglaFresh = (idTipo) => {
          const t = tiposA.find((x) => x?.idTipoAsistencia === idTipo || x?.IdTipoAsistencia === idTipo);
          return (t?.sigla || t?.Sigla || "").trim().toUpperCase();
        };

        const cuentaAsistenciaFresh = (idTipo) => {
          const sigla = getSiglaFresh(idTipo);
          return sigla === "P" || sigla === "L" || sigla === "R";
        };

        const calcAsistenciaFresh = (est) => {
          if (!asistenciasUnicas.length) return { nota: 0, asistidas: 0, total: 0 };
          const idRegistro = getRegistroId(est);
          if (!idRegistro) return { nota: 0, asistidas: 0, total: 0 };

          const fechasGrupo = new Set(asistenciasUnicas.map(a => a?.fechaAsistencia?.split?.("T")?.[0]).filter(Boolean));
          const totalClases = fechasGrupo.size;
          if (totalClases === 0) return { nota: 0, asistidas: 0, total: 0 };

          const fechasAsistidas = new Set();
          asistenciasUnicas.forEach((a) => {
            if (getAsistenciaRegistroId(a) !== idRegistro) return;
            if (cuentaAsistenciaFresh(a?.idTipoAsistencia || a?.IdTipoAsistencia)) {
              const fecha = a?.fechaAsistencia?.split?.("T")?.[0];
              if (fecha) fechasAsistidas.add(fecha);
            }
          });

          const clasesAsistidas = fechasAsistidas.size;
          return { nota: (clasesAsistidas * 5) / totalClases, asistidas: clasesAsistidas, total: totalClases };
        };

        // 2. Procesar Estudiantes V2 y Antiguos
        const registrosV2Mapeados = (dataV2 || []).map(r => ({ ...r, isV2: true }));
        const registrosAntiguosMapeados = (dataAntiguos || []).map(reg => ({
          ...reg,
          idRegistroNotasV2: reg.idRegistro || `temp-${reg.idEstudiante}-${reg.idGrupo}`,
          isV2: false,
          estadoV2: reg.estado || "Progreso",
          fechaRegistroV2: reg.fechaRegistro || new Date().toISOString(),
          proyectoIntegrador: reg.examenBloque1 || reg.speakingBloque1 || null,
          examenFinalEscrito: reg.examenBloque2 || null,
          examenFinalOral: reg.speakingBloque2 || null,
          tareas: reg.tareasBloque1 || reg.tareasBloque2 || null,
          participacion: reg.participacionBloque1 || reg.participacionBloque2 || null,
          asistenciaNota: null,
          notaFinal: reg.notaFinal || null,
          nivelModulo: calcularNivelModulo(reg.notaFinal || 0),
          habilitado: reg.habilitado !== undefined ? reg.habilitado : true,
        }));

        const todosLosRegistros = [...registrosV2Mapeados];
        registrosAntiguosMapeados.forEach(ant => {
          if (!todosLosRegistros.some(v2 => v2.idEstudiante === ant.idEstudiante)) {
            todosLosRegistros.push(ant);
          }
        });

        // Filtrar y unificar
        const uniqueItems = [];
        const seenStudents = new Set();
        todosLosRegistros
          .filter(est => (est.habilitado !== undefined ? est.habilitado === true : true) && (est.estudiante || est.Estudiante))
          .forEach((est) => {
            const estudiante = est.estudiante || est.Estudiante;
            const studentId = estudiante?.idEstudiante || estudiante?.IdEstudiante;
            if (studentId && !seenStudents.has(studentId)) {
              seenStudents.add(studentId);
              
              // 3. Calcular promedios con los datos frescos
              const notas = [
                n(est.participacion),
                n(est.tareas),
                n(est.proyectoIntegrador),
                n(est.examenFinalEscrito),
                n(est.examenFinalOral)
              ];
              const suma = notas.reduce((a, b) => a + b, 0);
              const { nota: asiNota } = calcAsistenciaFresh(est);
              const asisFinal = Number(asiNota.toFixed(2));
              let final = suma + asisFinal;
              if (final > 100) final = 100;

              uniqueItems.push({
                ...est,
                asistenciaNota: asisFinal,
                notaFinal: Number(final.toFixed(2)),
              });
            }
          });

        let estudiantesFinales = uniqueItems;

        // 4. Aplicar datos locales guardados si existen
        const datosLocales = cargarNotasLocal(idGrupo, idIdioma);
        if (datosLocales) {
          estudiantesFinales = estudiantesFinales.map(est => {
            const datoLocal = datosLocales.find(d => d.idRegistroNotasV2 === getRegistroId(est));
            if (datoLocal) {
              return { ...est, ...datoLocal };
            }
            return est;
          });
          setHasLocalData(true);
        }

        setEstudiantes(ordenarEstudiantesAlfabeticamente(estudiantesFinales));

      } catch (err) {
        console.error("Error al cargar todo:", err);
        showErrorAlert({ text: `Error al cargar datos del grupo: ${err.message}`, icon: "error" });
        setEstudiantes([]);
      } finally {
        setIsLoading(false);
      }
    };

    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoDocente, idIdioma, idGrupo]);


  const calcularPromedios = (est) => {
    // Nueva planilla:
    // Participación (15) + Tareas (20) + Proyecto Integrador (20)
    // + Examen Final Escrito (20) + Examen Final Oral (20) + Asistencia (5)

    const p = n(est.participacion);
    const t = n(est.tareas);
    const pi = n(est.proyectoIntegrador);
    const efE = n(est.examenFinalEscrito);
    const efO = n(est.examenFinalOral);
    const asistenciaCalc = calcularAsistenciaNota(est);
    const a = Math.min(n(asistenciaCalc.nota), 5);

    let notaFinalCalculada = p + t + pi + efE + efO + a;

    // Máximo teórico: 100 (incluye asistencia sobre 5)
    const notaFinal = Math.min(Number(notaFinalCalculada.toFixed(1)), 100);

    // Estado previo solo depende de si hay exámenes finales cargados
    const tieneExamenFinal =
      efE !== null && efE !== undefined &&
      efO !== null && efO !== undefined;

    const estadoPreview = !tieneExamenFinal
      ? "Progreso"
      : (notaFinal >= 70 ? "Aprobado" : "Reprobado");

    const nivelModulo = calcularNivelModulo(notaFinal);

    return {
      ...est,
      promedioPrimerExamen: null,
      promedioExamenFinal: null,
      asistenciaNotaCalculada: Number(a.toFixed(2)),
      clasesAsistidas: asistenciaCalc.asistidas,
      totalClases: asistenciaCalc.total,
      notaFinal,
      nivelModulo,
      // Para el preview en pantalla usamos SIEMPRE el estado calculado
      // El backend seguirá teniendo su propio EstadoV2 cuando se guarde.
      estadoPreview,
      tieneExamenFinal,
    };
  };

  const clamp = (campo, num) => {
    const max = limites[campo] ?? 100;
    if (num < 0) return 0;
    if (num > max) return max;
    return num;
  };

  const ordenarEstudiantesAlfabeticamente = (estudiantes) => {
    return estudiantes.sort((a, b) => {
      const nombreA = (a.estudiante?.nombre || '').trim();
      const nombreB = (b.estudiante?.nombre || '').trim();
      const apellidoA = (a.estudiante?.apellido || '').trim();
      const apellidoB = (b.estudiante?.apellido || '').trim();
      
      const compareNombre = nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
      if (compareNombre !== 0) return compareNombre;
      
      // Si los nombres son iguales, comparar apellidos
      return apellidoA.localeCompare(apellidoB, 'es', { sensitivity: 'base' });
    });
  };


  const handleNotaChange = (idRegistroNotasV2, campo, valor) => {
    const isEmpty = valor === "" || valor === null || valor === undefined;

    setEstudiantes((prev) => {
      const nuevosEstudiantes = prev.map((est) => {
        if (est.idRegistroNotasV2 !== idRegistroNotasV2) return est;
        let nuevo = { ...est };
        if (isEmpty) {
          nuevo[campo] = null;
        } else {
          // Convertir a número
          let num = Number(valor);
          if (Number.isNaN(num)) {
            nuevo[campo] = null;
          } else {
            const clamped = clamp(campo, num);
            if (clamped === 0) {
              nuevo[campo] = 0;
            } else {
              nuevo[campo] = clamped;
            }
          }
        }
        const calculado = calcularPromedios(nuevo);
        return calculado;
      });
      
      // Mantener orden alfabético
      const estudiantesOrdenados = ordenarEstudiantesAlfabeticamente(nuevosEstudiantes);
      
      // Guardar automáticamente en localStorage
      guardarNotasLocal(idGrupo, idIdioma, estudiantesOrdenados);
      setHasLocalData(true);
      
      return estudiantesOrdenados;
    });
  };

  const handleConfirmActualizarTodasLasNotas = () => setShowModal(true);

  const handleActualizarTodasLasNotas = async () => {
    if (!estudiantes?.length) {
      showErrorAlert({ text: "No hay estudiantes para subir notas.", icon: "error" });
      setShowModal(false);
      return;
    }

    setIsUploading(true);
    
    try {
      await Promise.all(
        estudiantes.map(async (est) => {
          const tieneExamenFinal =
            est.examenFinalEscrito !== null &&
            est.examenFinalEscrito !== undefined &&
            est.examenFinalOral !== null &&
            est.examenFinalOral !== undefined;

          const enviarValor = (valor) => {
            if (valor === null || valor === undefined) return null;
            if (valor === 0 || Number(valor) === 0) return 0;
            const num = Number(valor);
            return Number.isNaN(num) ? null : num;
          };

          const dataNota = {
            idRegistroNotasV2: est.idRegistroNotasV2,
            fechaRegistroV2: est.fechaRegistroV2 || new Date().toISOString(),
            notaFinal: est.notaFinal !== null && est.notaFinal !== undefined
              ? Number(est.notaFinal.toFixed(1))
              : null,
            totalPrecio: est.totalPrecio ?? 0,
            idGrupo: est.idGrupo,
            idEstudiante: est.idEstudiante,
            proyectoIntegrador: enviarValor(est.proyectoIntegrador),
            examenFinalEscrito: enviarValor(est.examenFinalEscrito),
            examenFinalOral: enviarValor(est.examenFinalOral),
            tareas: enviarValor(est.tareas),
            participacion: enviarValor(est.participacion),
            asistenciaNota: enviarValor(Number(calcularAsistenciaNota(est).nota.toFixed(2))),
            nivelModulo: est.nivelModulo || calcularNivelModulo(est.notaFinal) || "",
          };

          if (est.isV2) {
            await actualizarRegistroNotaV2(est.idRegistroNotasV2, dataNota);
          } else {
            console.log("Creando nuevo registro V2 para el estudiante:", est.idEstudiante);
            await crearRegistroV2(dataNota);
          }
        })
      );

      showActualizadoCorrectamenteAlert({
        text: "Todas las notas se actualizaron con éxito.",
        icon: "success",
      });

      limpiarNotasLocal(idGrupo, idIdioma);
      setHasLocalData(false);

    } catch (err) {
      const errorMessage = err?.response?.data?.message || err.message;
      showErrorAlert({ text: `Error al subir notas: ${errorMessage}`, icon: "error" });
    } finally {
      setIsUploading(false);
    }

    setShowModal(false);
  };

  const handleDescargarExcel = async () => {
    try {
      await descargarNotasGrupo(idGrupo);
    } catch (err) {
      showErrorAlert({ text: "Error al descargar el archivo de notas", icon: "error" });
    }
  };

  return (
    <ContenedorPageTemplate fondo={false} objetos={estudiantes}>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-md shadow-md">
            <h2 className="text-lg font-extrabold mb-4">Confirmación</h2>
            <p>¿Estás seguro de que deseas subir todas las notas?</p>
            <p>Si un alumno no tiene Examen Final, quedará en <b>Progreso</b> y <b>NO</b> se cambiará su estado.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
              >
                Cancelar
              </button>
              <button
                onClick={handleActualizarTodasLasNotas}
                disabled={isUploading}
                className={`px-4 py-2 rounded-md text-white ${
                  isUploading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {isUploading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Confirmando...
                  </div>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full bg-white shadow-lg rounded-lg p-3 md:p-6 mx-auto" style={{ maxWidth: "98%" }}>
        <div className="text-center mb-4 md:mb-6 relative">
          <div className="absolute left-0 top-0">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 rounded-lg py-1.5 px-3 flex items-center gap-1 font-medium transition-colors"
              title="Volver atrás"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Volver
            </button>
          </div>
          <h2 className="text-lg md:text-2xl font-bold text-blue-900">
            Planilla de Notas : {grupoDocente?.nombre || "–"}
          </h2>
          <p className="text-xs md:text-sm text-gray-600 mt-2">
            💡 Desliza horizontalmente para ver todas las columnas
          </p>
        </div>

        <div className="overflow-x-auto -mx-3 md:mx-0 rounded-lg border border-gray-300 shadow-sm">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600 mb-3"></div>
              <p className="text-blue-800 font-medium animate-pulse">Cargando notas de estudiantes...</p>
            </div>
          ) : (
          <div className="inline-block min-w-full align-middle">
            <table className="table-auto border-collapse w-full border border-black text-xs md:text-sm">
              <thead className="bg-blue-700 text-white sticky top-0 z-10">
                <tr>
                  <th rowSpan="2" className="sticky left-0 z-20 bg-blue-700 px-2 md:px-3 py-2 border border-black align-middle min-w-[100px] md:min-w-[120px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]">Estudiante</th>
                  <th className="px-1 md:px-2 py-2 border border-black text-xs min-w-[60px] md:min-w-[70px]">Participación</th>
                  <th className="px-1 md:px-2 py-2 border border-black min-w-[60px] md:min-w-[70px] text-xs">Tareas</th>
                  <th className="px-1 md:px-2 py-2 border border-black text-xs min-w-[70px] md:min-w-[80px]">Proyecto Integrador</th>
                  <th colSpan="2" className="px-1 md:px-2 py-2 border border-black text-center text-xs md:text-sm">Examen Final</th>
                  <th rowSpan="2" className="px-1 md:px-2 py-2 border border-black align-middle min-w-[60px] md:min-w-[70px] text-xs">Asistencia (5%)</th>
                  <th rowSpan="2" className="px-1 md:px-2 py-2 border border-black align-middle min-w-[70px] md:min-w-[80px] text-xs md:text-sm">Nota Final</th>
                  <th rowSpan="2" className="px-1 md:px-2 py-2 border border-black align-middle min-w-[80px] md:min-w-[90px] text-xs md:text-sm">Nivel</th>
                  <th rowSpan="2" className="px-1 md:px-2 py-2 border border-black align-middle min-w-[70px] md:min-w-[80px] text-xs md:text-sm">Estado</th>
                </tr>
                <tr>
                  <th className="px-1 md:px-2 py-2 border border-black text-xs min-w-[60px] md:min-w-[70px]">(15%)</th>
                  <th className="px-1 md:px-2 py-2 border border-black min-w-[60px] md:min-w-[70px] text-xs">(20%)</th>
                  <th className="px-1 md:px-2 py-2 border border-black min-w-[70px] md:min-w-[80px] text-xs">(20%)</th>
                  <th className="px-1 md:px-2 py-2 border border-black min-w-[60px] md:min-w-[70px] text-xs">Escrito</th>
                  <th className="px-1 md:px-2 py-2 border border-black min-w-[60px] md:min-w-[70px] text-xs">Oral</th>
                </tr>
              </thead>
            <tbody>
              {estudiantes?.length ? (
                estudiantes.map((est) => {
                  const inputProps = (campo) => {
                    const isParticipacion = campo === 'participacion';
                    return {
                      type: "number",
                      inputMode: "decimal",
                      step: "any",
                      min: 0,
                      max: limites[campo] ?? 100,
                      className: `w-full border border-gray-300 rounded p-1 text-center text-xs md:text-sm ${
                        isParticipacion ? 'min-w-[50px] md:min-w-[55px]' : 'min-w-[60px] md:min-w-[70px]'
                      }`,
                      onChange: (e) => handleNotaChange(est.idRegistroNotasV2, campo, e.target.value),
                      onWheel: (e) => e.currentTarget.blur(),
                    };
                  };

                  return (
                    <tr key={est.idRegistroNotasV2} className="text-center hover:bg-blue-50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white border border-black px-2 py-2 min-w-[100px] md:min-w-[120px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <div className="text-xs md:text-sm font-medium text-gray-800">
                          {est.estudiante?.nombre} {est.estudiante?.apellido}
                        </div>
                      </td>

                      {/* Participación y Tareas */}
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2"><input {...inputProps("participacion")} value={V(est.participacion)} /></td>
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2"><input {...inputProps("tareas")} value={V(est.tareas)} /></td>

                      {/* Proyecto Integrador */}
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2"><input {...inputProps("proyectoIntegrador")} value={V(est.proyectoIntegrador)} /></td>

                      {/* Examen Final */}
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2"><input {...inputProps("examenFinalEscrito")} value={V(est.examenFinalEscrito)} /></td>
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2"><input {...inputProps("examenFinalOral")} value={V(est.examenFinalOral)} /></td>
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2 text-xs md:text-sm font-semibold bg-slate-50">
                        {est.totalClases > 0
                          ? `${Number(est.asistenciaNotaCalculada || 0).toFixed(2)} (${est.clasesAsistidas}/${est.totalClases})`
                          : "0.00 (0/0)"}
                      </td>

                      {/* Nota Final, Nivel y Estado */}
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2 text-xs md:text-sm font-bold text-blue-600 bg-blue-100">
                        {est.notaFinal !== null && est.notaFinal !== undefined ? est.notaFinal.toFixed(1) : "-"}
                      </td>
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2 text-xs md:text-sm font-semibold">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          est.nivelModulo === 'Excelente' ? 'bg-purple-100 text-purple-800' :
                          est.nivelModulo === 'Satisfactorio' ? 'bg-blue-100 text-blue-800' :
                          est.nivelModulo === 'Bueno' ? 'bg-green-100 text-green-800' :
                          est.nivelModulo === 'Suficiente' ? 'bg-yellow-100 text-yellow-800' :
                          est.nivelModulo === 'Insuficiente' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {est.nivelModulo || "NO"}
                        </span>
                      </td>
                      <td className="border border-black px-1 md:px-2 py-1 md:py-2 text-xs md:text-sm font-semibold">
                        <span className={`px-2 py-1 rounded-full ${
                          est.estadoPreview === 'Aprobado' ? 'bg-green-100 text-green-800' :
                          est.estadoPreview === 'Reprobado' ? 'bg-red-100 text-red-800' :
                          est.estadoPreview === 'Culminado' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {est.estadoPreview}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="border border-black px-2 py-4 text-center text-gray-600 text-xs md:text-sm">
                    No hay estudiantes registrados en este grupo
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 md:gap-4 mt-4 md:mt-6">
          {estudiantes.length > 0 && (
            <>
              <button
                onClick={() => setShowModal(true)}
                disabled={isUploading}
                className={`w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 rounded-lg text-white text-sm md:text-base font-semibold transition-all ${
                  isUploading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-yellow-500 hover:bg-yellow-600 hover:shadow-lg'
                }`}
              >
                {isUploading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Guardando...
                  </div>
                ) : (
                  <>
                    📤 Subir Notas
                    {hasLocalData && <span className="ml-2 text-xs">(Datos sin guardar)</span>}
                  </>
                )}
              </button>
            </>
          )}

          <button
            onClick={handleDescargarExcel}
            className="w-full sm:w-auto bg-blue-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-blue-600 hover:shadow-lg transition-all"
          >
            📊 Descargar Excel
          </button>
        </div>
      </div>
    </ContenedorPageTemplate>
  );
};
